import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import posed from 'react-pose';
import DriveEtaIcon from '@material-ui/icons/DriveEta';
import emphasize from '../../../animations/emphasize';
import PatientIcon from '../../../patient/PatientIcon';
import { TIMELINE_CARD_ATTRIBUTE } from './useTimelinePageSize';

/*
 * Visual twin of the card on the original Waiting Room screen (`PatientSlot` in
 * WaitingRoomMonitor.jsx): translucent row, patient icon, short name, green "ready" border, and
 * a cyan highlight that replays whenever the patient changes room. The journey status is left
 * out because the phase column already conveys it, and the sizing differs since the card lives
 * inside a column instead of spanning the screen.
 */

const readyColor = theme => theme.successColor || theme.colors.primary;

const Slot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
  min-width: 0;
`;

const SlotContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;

  font-size: ${props => props.$fontSizeVw}vw;
  font-weight: 100;
  line-height: 1.3;
  color: ${props => props.theme.textColor.string()};

  padding: 0.5em 0.6em;
  border-radius: 0.25rem;
  border: 2px solid transparent;
  background-color: ${props => (props.$zebra ? 'transparent' : props.theme.textColor.alpha(0.07).string())};

  ${props =>
    props.$withHighlightBorder &&
    css`
      background-color: ${readyColor(props.theme).alpha(0.5).string()} !important;
      border-color: ${readyColor(props.theme).string()};
    `}

  @media (max-height: 700px) {
    font-size: ${props => props.$fontSizeVw * 0.85}vw;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
`;

const PatientIconWrapper = styled.div`
  flex: 0 0 auto;
  margin-right: 0.5em;
  display: flex;
  align-items: center;
`;

const Name = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const NameText = styled.span`
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PickupIcon = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  margin-left: 0.2em;
  font-size: 0.85em;
  line-height: 1;
`;

const HighlightWrapper = styled.div`
  position: absolute;
  top: -0.125rem;
  bottom: -0.125rem;
  left: -0.125rem;
  right: -0.125rem;
  pointer-events: none;
`;

const Highlight = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 0.25rem;
  border: 0.125rem solid transparent;
  animation: 1s ${emphasize} ease-out alternate 12;
`;

const TimelinePatientCard = forwardRef(
  ({ icon, color, name, readyForPickup, withHighlightBorder, highlightKey, zebra, fontSizeVw, style }, ref) => (
    <Slot ref={ref} style={style} {...{ [TIMELINE_CARD_ATTRIBUTE]: 'true' }}>
      <SlotContent $withHighlightBorder={withHighlightBorder} $zebra={zebra} $fontSizeVw={fontSizeVw}>
        <Row>
          <PatientIconWrapper>
            <PatientIcon backgroundColor={color} icon={icon} inline />
          </PatientIconWrapper>
          <Name>
            <NameText>{name}</NameText>
            {readyForPickup && (
              <PickupIcon aria-label="Ready for pickup">
                <DriveEtaIcon fontSize="inherit" />
              </PickupIcon>
            )}
          </Name>
        </Row>
        <HighlightWrapper>
          {/* Re-keyed on room change so the emphasis animation replays, exactly like the original */}
          <Highlight key={highlightKey} />
        </HighlightWrapper>
      </SlotContent>
    </Slot>
  )
);

TimelinePatientCard.displayName = 'TimelinePatientCard';

export const AnimatedTimelinePatientCard = posed(TimelinePatientCard)({
  preEnter: {
    opacity: 0,
    transition: { type: 'spring' },
  },
  enter: {
    position: 'relative',
    opacity: 1,
    y: 0,
    transition: {
      duration: 500,
      ease: [0.0, 0.01, 0.01, 0.99],
    },
  },
  exit: {
    position: 'relative',
    opacity: 0,
    y: -20,
    transition: {
      duration: 500,
      ease: [0.0, 0.01, 0.01, 0.99],
    },
  },
});

export default TimelinePatientCard;
